import { getSiteConfig } from '@shared/config';
import { parsePhoneNumber, type CountryCode } from 'libphonenumber-js';
import { formatPhoneNumberIntl, isValidPhoneNumber } from 'react-phone-number-input';

type PhoneNumberProfileFields = {
  country?: string;
  countryCode?: string;
  mobilePhoneNumber?: string;
  /** Fallback when profile slices are not loaded yet (e.g. SSR). */
  mobileNumber?: string;
};

type PhoneNumberInputValue = {
  prefix: string;
  number: string;
};

function extractDialCodeFromPrefix(prefix: string): string {
  return prefix.split('+')[1]?.replace(/\D/g, '') || '';
}

function extractCountryFromPrefix(prefix: string): string {
  return prefix.split('+')[0] || '';
}

function buildE164FromParts(prefix: string, number: string): string {
  const dialCode = extractDialCodeFromPrefix(prefix);
  const rawDigits = number.replace(/\D/g, '');

  return dialCode ? `+${dialCode}${rawDigits}` : `+${rawDigits}`;
}

function getNationalTrunkPrefix(): string | undefined {
  return getSiteConfig().PHONE_NUMBER.nationalTrunkPrefix;
}

/** Strips non-digits and prepends the trunk digit when the API stores a national mobile without it (e.g. 412312312 → 0412312312). */
function normaliseNationalMobileDigits(storedDigits: string): string {
  const nationalPrefix = getNationalTrunkPrefix();
  const digits = storedDigits.replace(/\D/g, '');

  if (!digits) {
    return nationalPrefix ?? '';
  }

  const trunkDigit = nationalPrefix?.charAt(0);
  const nationalPrefixWithoutTrunk = nationalPrefix?.slice(1);

  if (nationalPrefix && trunkDigit && nationalPrefixWithoutTrunk && !digits.startsWith(nationalPrefix) && digits.startsWith(nationalPrefixWithoutTrunk) && digits.length === 9) {
    return `${trunkDigit}${digits}`;
  }

  return digits;
}

function groupsFromPlaceholder(placeholder: string): number[] | null {
  const parts = placeholder.trim().split(/\s+/);
  if (parts.length < 2 || !parts.every(part => /^[0-9X]+$/.test(part))) {
    return null;
  }

  return parts.map(part => part.length);
}

function formatGroupedNationalDigits(digits: string, groups: number[]): string {
  const normalised = digits.replace(/\D/g, '').slice(
    0,
    groups.reduce((sum, size) => sum + size, 0)
  );
  if (!normalised) {
    return '';
  }

  const match = normalised.match(new RegExp(`^(${groups.map(size => `\\d{0,${size}}`).join(')(')})`));
  if (!match) {
    return normalised;
  }

  return match.slice(1).filter(Boolean).join(' ');
}

function formatNationalPhoneNumberParts(prefix: string, number: string): string {
  const rawDigits = number.replace(/\D/g, '');
  if (!rawDigits) {
    return '';
  }

  const groups = groupsFromPlaceholder(getSiteConfig().PHONE_NUMBER.placeholder);

  if (groups) {
    return formatGroupedNationalDigits(rawDigits, groups);
  }

  try {
    const country = extractCountryFromPrefix(prefix);
    const e164 = buildE164FromParts(prefix, number);
    const parsed = country.length === 2 ? parsePhoneNumber(e164, country as CountryCode) : parsePhoneNumber(e164);

    return parsed.formatNational();
  } catch {
    return number;
  }
}

function formatNationalPhoneNumberFromE164(e164: string): string {
  if (!e164) {
    return '';
  }

  try {
    return parsePhoneNumber(e164).formatNational();
  } catch {
    return e164;
  }
}

function isValidMobileNumberParts(prefix: string, number: string, nationalNumberRegex?: RegExp): boolean {
  if (!prefix || !number) {
    return true;
  }

  const nationalDigits = number.replace(/\D/g, '');
  if (nationalNumberRegex) {
    return nationalNumberRegex.test(nationalDigits);
  }

  try {
    return isValidPhoneNumber(buildE164FromParts(prefix, number));
  } catch {
    return false;
  }
}

type PhoneVerificationSendFields = {
  country?: string;
  countryCode?: string;
  nationalDigits: string;
};

/** Single formatter for `MY_ACCOUNT.VERIFICATION.SEND` phone `value` (AU national digits; UK E.164). */
function formatPhoneForVerificationSend({ country, countryCode, nationalDigits }: PhoneVerificationSendFields): string {
  const phoneConfig = getSiteConfig().PHONE_NUMBER;
  let digits = nationalDigits.replace(/\D/g, '');

  if (!phoneConfig.showCountryPrefix && country === phoneConfig.country) {
    return normaliseNationalMobileDigits(digits);
  }

  if (digits.startsWith('0')) {
    digits = digits.slice(1);
  }

  const dialCode = countryCode?.replace(/\D/g, '') ?? '';

  return dialCode ? `+${dialCode}${digits}` : `+${digits}`;
}

/** Verification send `value` from `PhoneNumberInput` `{ prefix, number }`. */
function phoneNumberForVerificationFromInput(prefix: string, number: string): string {
  return formatPhoneForVerificationSend({
    country: extractCountryFromPrefix(prefix),
    countryCode: extractDialCodeFromPrefix(prefix),
    nationalDigits: number,
  });
}

/** Profile phone string for the verify-SMS send API. */
function phoneNumberForVerification(user: PhoneNumberProfileFields): string {
  const { mobilePhoneNumber, countryCode, country } = user;

  if (mobilePhoneNumber && countryCode) {
    return formatPhoneForVerificationSend({
      country,
      countryCode,
      nationalDigits: mobilePhoneNumber,
    });
  }

  return user.mobileNumber ?? '';
}

function formatProfileMobileNumberForDisplay(e164: string): string {
  if (!e164) {
    return '';
  }

  const phoneConfig = getSiteConfig().PHONE_NUMBER;

  return phoneConfig.showCountryPrefix ? formatPhoneNumberIntl(e164) : formatNationalPhoneNumberFromE164(e164);
}

/** `{ prefix, number }` for `PhoneNumberInput` — national markets keep profile digits as-is. */
function phoneNumberForInput(country?: string, countryCode?: string, mobilePhoneNumber?: string): PhoneNumberInputValue {
  const phoneConfig = getSiteConfig().PHONE_NUMBER;

  if (!country || !countryCode || !mobilePhoneNumber) {
    if (!phoneConfig.showCountryPrefix) {
      return { prefix: phoneConfig.defaultPrefix, number: phoneConfig.nationalTrunkPrefix ?? '' };
    }

    return { prefix: '', number: '' };
  }

  const prefix = `${country}+${countryCode}`;

  if (!phoneConfig.showCountryPrefix && country === phoneConfig.country) {
    return { prefix, number: formatNationalPhoneNumberParts(prefix, normaliseNationalMobileDigits(mobilePhoneNumber)) };
  }

  const e164 = `+${countryCode}${mobilePhoneNumber}`;
  const intlFormatted = formatPhoneNumberIntl(e164)!;
  const number = intlFormatted.replace(new RegExp(`^\\+${countryCode}\\s*`), '');

  return { prefix, number };
}

/** User-visible phone string for OTP / confirmation copy. */
function formatPhoneNumberForDisplay(prefix: string, number: string): string {
  const phoneConfig = getSiteConfig().PHONE_NUMBER;

  if (!number) {
    return '';
  }

  if (!phoneConfig.showCountryPrefix) {
    return formatNationalPhoneNumberParts(prefix, number);
  }

  const dialCode = extractDialCodeFromPrefix(prefix);

  return dialCode ? `+${dialCode} ${number}` : number;
}

function isSamePhoneForVerificationChange(prefix: string, number: string, originalVerificationValue: string): boolean {
  if (!originalVerificationValue) {
    return false;
  }

  return phoneNumberForVerificationFromInput(prefix, number) === originalVerificationValue;
}

export {
  formatNationalPhoneNumberFromE164,
  formatNationalPhoneNumberParts,
  formatPhoneNumberForDisplay,
  formatProfileMobileNumberForDisplay,
  isSamePhoneForVerificationChange,
  isValidMobileNumberParts,
  phoneNumberForInput,
  phoneNumberForVerification,
  phoneNumberForVerificationFromInput,
};
export type { PhoneNumberInputValue, PhoneNumberProfileFields };
